import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Container,
  Grid,
  Tabs,
  Tab,
  Box,
  Typography,
  Skeleton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectActiveRequests } from "../../../../Redux/Reducers/selectors";
import FundRebalance from "./RebalancingInfo/FundRebalance";
import SleeveReblance from "./RebalancingInfo/SleeveRebalance";
import FundRebalanceStep1 from "./RebalancingInfo/FundRebalance/Step1";
import FundRebalanceStep2 from "./RebalancingInfo/FundRebalance/Step2";
import FundRebalanceStep3 from "./RebalancingInfo/FundRebalance/Step3";
import FundRebalanceStep4 from "./RebalancingInfo/FundRebalance/Step4";
import FundRebalanceStep5 from "./RebalancingInfo/FundRebalance/Step5";
import FundRebalanceStep6 from "./RebalancingInfo/FundRebalance/Step6";
import FundRebalanceStep7 from "./RebalancingInfo/FundRebalance/Step7";
import SwapStep1 from "./RebalancingInfo/SwapRebalance/SwapStep1";
import SwapStep2 from "./RebalancingInfo/SwapRebalance/SwapStep2";
import SwapStep3 from "./RebalancingInfo/SwapRebalance/SwapStep3";
import SwapStep5 from "./RebalancingInfo/SwapRebalance/SwapStep5";

const RebalancerAllocation = forwardRef(({ onStepChange }, ref) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [rebalancedFunds, setSelectedRebalancedFunds] = useState([]);
  const [rebalancedStrategies, setRebalancedStrategies] = useState([]);
  const [selectedSwapContract, setSelectedSwapContract] = useState([]);
  const [rebalanceType, setRebalanceType] = useState("regular");
  const [executionStyles, setExecutionStyles] = useState({});
  const [deltaRebalancer, setDeltaRebalancer] = useState(0);
  const [deltaValue, setDeltaValue] = useState(1);
  const [driftValue, setDriftValue] = useState(1);

  // Get active requests to check for loading states
  const activeRequests = useSelector(selectActiveRequests);

  // Check if any rebalancer-related loading is happening
  // More efficient: check for any request that starts with common rebalancer patterns
  const rebalancerLoadingPatterns = [
    "rebalancer_check_blacklist",
    "create_rebalancer_items",
    "create_fund_rebalancer_trades",
    "create_rebalancer_crossing_orders",
    "rebalancer_locates",
    "rebalance_swaps",
  ];

  const isLoading = activeRequests.some((request) =>
    rebalancerLoadingPatterns.some((pattern) => request.includes(pattern))
  );

  // Notify parent when step changes from 0 to any other step
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep, rebalanceType);
    }
  }, [currentStep, rebalanceType, onStepChange]);

  // Expose restart function to parent via ref
  useImperativeHandle(ref, () => ({
    restart: handleBackToStart
  }));

  const handleRebalanceTypeChange = (type) => {
    setRebalanceType(type);
    // Notify parent when rebalance type changes
    if (onStepChange) {
      onStepChange(currentStep, type);
    }
  };

  const handleBackToStart = () => {
    setCurrentStep(0);
    setSelectedRebalancedFunds([]);
    setRebalancedStrategies([]);
    setSelectedSwapContract([]);
    setRebalanceType("regular");
    setExecutionStyles({});
    setDeltaRebalancer(0);
    setDeltaValue(1);
    setDriftValue(1);
  };

  return (
    <Container maxWidth="100%" sx={{ marginTop: 2, marginBottom: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Box sx={{ width: "100%" }}>
            <Box>
              {currentStep === 0 ? (
                <SleeveReblance
                  setCurrentStep={setCurrentStep}
                  rebalancedFunds={rebalancedFunds}
                  setSelectedRebalancedFunds={setSelectedRebalancedFunds}
                  rebalancedStrategies={rebalancedStrategies}
                  setRebalancedStrategies={setRebalancedStrategies}
                  onRebalanceTypeChange={handleRebalanceTypeChange}
                  selectedSwapContract={selectedSwapContract}
                  setSelectedSwapContract={setSelectedSwapContract}
                  deltaRebalancer={deltaRebalancer}
                  setDeltaRebalancer={setDeltaRebalancer}
                  deltaValue={deltaValue}
                  setDeltaValue={setDeltaValue}
                  driftValue={driftValue}
                  setDriftValue={setDriftValue}
                />
              ) : rebalanceType === "swap" ? (
                currentStep === 12 ? (
                  <SwapStep1
                    setCurrentStep={setCurrentStep}
                    contracts={selectedSwapContract}
                  />
                ) : currentStep === 1 ? (
                  <SwapStep2
                    setCurrentStep={setCurrentStep}
                    contracts={selectedSwapContract}
                    setExecutionStyles={setExecutionStyles}
                  />
                ) : currentStep === 2 ? (
                  <SwapStep3
                    setCurrentStep={setCurrentStep}
                    contracts={selectedSwapContract}
                    executionStyles={executionStyles}
                  />
                ) : currentStep === 3 ? (
                  <SwapStep5
                    setCurrentStep={setCurrentStep}
                    contracts={selectedSwapContract}
                  />
                ) : (
                  <></>
                )
              ) : currentStep === 1 ? (
                <FundRebalanceStep4
                  setCurrentStep={setCurrentStep}
                  sleeve={true}
                  rebalanceType={rebalanceType}
                  rebalancedFunds={rebalancedFunds}
                  rebalancedStrategies={rebalancedStrategies}
                  deltaRebalancer={deltaRebalancer}
                  deltaValue={deltaValue}
                  driftValue={driftValue}
                  setDriftValue={setDriftValue}
                />
              ) : currentStep === 2 ? (
                <FundRebalanceStep1
                  setCurrentStep={setCurrentStep}
                  sleeve={true}
                  driftValue={driftValue}
                  rebalanceType={rebalanceType}
                  rebalancedFunds={rebalancedFunds}
                  rebalancedStrategies={rebalancedStrategies}
                  deltaRebalancer={deltaRebalancer}
                />
              ) : currentStep === 3 ? (
                <FundRebalanceStep6
                  onPrevious={() => setCurrentStep(2)}
                  setCurrentStep={setCurrentStep}
                />
              ) : currentStep === 4 ? (
                <FundRebalanceStep2
                  onPrevious={() => setCurrentStep(3)}
                  setCurrentStep={setCurrentStep}
                />
              ) : currentStep === 5 ? (
                <FundRebalanceStep5
                  onPrevious={() => setCurrentStep(4)}
                  setCurrentStep={setCurrentStep}
                  rebalancedFunds={rebalancedFunds}
                  rebalanceType={rebalanceType}
                />
              ) : (
                <FundRebalanceStep7
                  rebalancedFunds={rebalancedFunds}
                  rebalanceType={rebalanceType}
                />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
});

export default RebalancerAllocation;
